import { MessageSquare, Check } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

import type { Notification } from '../types/notification.types';

interface DialogNotificationDialogProps {
  notification: Notification;
  open: boolean;
  onClose: () => void;
  onMarkAsRead: (id: string) => void;
}

export const DialogNotificationDialog: React.FC<
  DialogNotificationDialogProps
> = ({ notification, open, onClose, onMarkAsRead }) => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState(false);

  // Set up avatar URL when notification changes
  useEffect(() => {
    if (notification?.action_user_id?.avatar) {
      const baseUrl = import.meta.env.VITE_HEALTHY_EMPIRE_SERVER || '';
      const url = `${baseUrl}/public/avatar/thumb/${notification.action_user_id.avatar}`;

      // Try to fetch the image to check if it exists
      fetch(url, { method: 'HEAD' })
        .then((response) => {
          if (response.ok) {
            setAvatarUrl(url);
            setAvatarError(false);
          } else {
            setAvatarError(true);
          }
        })
        .catch(() => {
          setAvatarError(true);
        });
    } else {
      setAvatarUrl(null);
    }
  }, [notification]);

  const handleMarkAsRead = () => {
    onMarkAsRead(notification._id);
    onClose();
  };

  // Get user initials for avatar fallback
  const getUserInitials = useCallback(() => {
    if (!notification.action_user_id?.empName) return '??';

    const nameParts = notification.action_user_id.empName.split(' ');
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();

    return (
      nameParts[0].charAt(0).toUpperCase() +
      nameParts[nameParts.length - 1].charAt(0).toUpperCase()
    );
  }, [notification.action_user_id?.empName]);

  // Handle avatar load error
  const handleAvatarError = () => {
    setAvatarError(true);
  };

  // Simplified markdown rendering
  const renderMarkdown = () => {
    if (!notification.message) return null;

    // Split message into lines for processing
    const lines = notification.message.split('\n');
    const elements: React.ReactNode[] = [];

    let inList = false;
    let listItems: React.ReactNode[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Empty line - add spacing
      if (!line) {
        if (inList) {
          elements.push(
            <ul key={`list-${i}`} className="my-2 space-y-1 text-sm">
              {listItems}
            </ul>,
          );
          inList = false;
          listItems = [];
        }
        elements.push(<div key={`space-${i}`} className="h-2" />);
        continue;
      }

      // Handle horizontal rule
      if (line === '---' || line === '***' || line === '___') {
        if (inList) {
          elements.push(
            <ul key={`list-${i}`} className="my-2 space-y-1 text-sm">
              {listItems}
            </ul>,
          );
          inList = false;
          listItems = [];
        }
        elements.push(
          <hr key={`hr-${i}`} className="my-3 border-t border-border" />,
        );
        continue;
      }

      // Handle headings
      if (line.startsWith('# ')) {
        if (inList) {
          elements.push(
            <ul key={`list-${i}`} className="my-2 space-y-1 text-sm">
              {listItems}
            </ul>,
          );
          inList = false;
          listItems = [];
        }
        elements.push(
          <h1 key={i} className="text-lg font-semibold text-foreground">
            {processInlineMarkdown(line.substring(2))}
          </h1>,
        );
        continue;
      }

      if (line.startsWith('## ')) {
        if (inList) {
          elements.push(
            <ul key={`list-${i}`} className="my-2 space-y-1 text-sm">
              {listItems}
            </ul>,
          );
          inList = false;
          listItems = [];
        }
        elements.push(
          <h2 key={i} className="text-base font-medium text-foreground">
            {processInlineMarkdown(line.substring(3))}
          </h2>,
        );
        continue;
      }

      // Handle list items
      if (line.startsWith('- ') || line.startsWith('* ')) {
        inList = true;
        const content = line.substring(2);
        listItems.push(
          <li key={`item-${i}`} className="flex items-start gap-2">
            <span className="mt-1 text-primary">•</span>
            <span className="flex-1">{processInlineMarkdown(content)}</span>
          </li>,
        );
        continue;
      }

      // Handle numbered list items
      const numberedListMatch = line.match(/^(\d+)\.\s+(.+)$/);
      if (numberedListMatch) {
        inList = true;
        const [, number, content] = numberedListMatch;
        listItems.push(
          <li key={`item-${i}`} className="flex items-start gap-2">
            <span className="mt-0.5 min-w-5 font-medium text-primary">
              {number}.
            </span>
            <span className="flex-1">{processInlineMarkdown(content)}</span>
          </li>,
        );
        continue;
      }

      // Handle regular paragraphs
      if (inList) {
        elements.push(
          <ul key={`list-${i}`} className="my-2 space-y-1 text-sm">
            {listItems}
          </ul>,
        );
        inList = false;
        listItems = [];
      }

      elements.push(
        <p key={i} className="text-sm text-muted-foreground">
          {processInlineMarkdown(line)}
        </p>,
      );
    }

    // Add any remaining list items
    if (inList && listItems.length > 0) {
      elements.push(
        <ul key="final-list" className="my-2 space-y-1 text-sm">
          {listItems}
        </ul>,
      );
    }

    return <div className="space-y-2">{elements}</div>;
  };

  // Process inline markdown (bold, italic, code)
  const processInlineMarkdown = (text: string): React.ReactNode => {
    if (!text) return null;

    const parts: React.ReactNode[] = [];
    let currentText = '';
    let i = 0;

    while (i < text.length) {
      // Bold text with **
      if (text.substring(i, i + 2) === '**' && i + 2 < text.length) {
        if (currentText) {
          parts.push(<span key={`text-${i}`}>{currentText}</span>);
          currentText = '';
        }

        const startBold = i + 2;
        let endBold = text.indexOf('**', startBold);
        if (endBold === -1) endBold = text.length;

        const boldText = text.substring(startBold, endBold);
        parts.push(
          <strong key={`bold-${i}`} className="font-semibold text-foreground">
            {boldText}
          </strong>,
        );

        i = endBold + 2;
        continue;
      }

      // Italic text with *
      if (text[i] === '*' && text[i + 1] !== '*' && i + 1 < text.length) {
        if (currentText) {
          parts.push(<span key={`text-${i}`}>{currentText}</span>);
          currentText = '';
        }

        const startItalic = i + 1;
        let endItalic = text.indexOf('*', startItalic);
        if (endItalic === -1) endItalic = text.length;

        const italicText = text.substring(startItalic, endItalic);
        parts.push(
          <em key={`italic-${i}`} className="text-muted-foreground">
            {italicText}
          </em>,
        );

        i = endItalic + 1;
        continue;
      }

      // Code with `
      if (text[i] === '`') {
        if (currentText) {
          parts.push(<span key={`text-${i}`}>{currentText}</span>);
          currentText = '';
        }

        const startCode = i + 1;
        let endCode = text.indexOf('`', startCode);
        if (endCode === -1) endCode = text.length;

        const codeText = text.substring(startCode, endCode);
        parts.push(
          <code
            key={`code-${i}`}
            className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs"
          >
            {codeText}
          </code>,
        );

        i = endCode + 1;
        continue;
      }

      // Regular text
      currentText += text[i];
      i++;
    }

    // Add any remaining text
    if (currentText) {
      parts.push(<span key="final-text">{currentText}</span>);
    }

    return parts.length > 0 ? <>{parts}</> : text;
  };

  // Prevent dialog from closing when clicking outside or pressing escape
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      return;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" hiddenCloseIcon={true}>
        <DialogHeader className="flex flex-row items-center gap-3 pb-4">
          <div className="flex-shrink-0">
            <Avatar className="h-10 w-10 border-2 border-border">
              {avatarUrl && !avatarError ? (
                <AvatarImage
                  src={avatarUrl}
                  alt={notification.action_user_id?.empName || 'User'}
                  onError={handleAvatarError}
                />
              ) : (
                <AvatarFallback className="bg-muted font-medium">
                  {getUserInitials()}
                </AvatarFallback>
              )}
            </Avatar>
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-lg font-semibold text-foreground">
              {notification.title}
            </DialogTitle>
            <DialogDescription className="mt-1 text-xs text-muted-foreground">
              {notification.action_user_id?.empName && (
                <span className="font-medium text-foreground">
                  {notification.action_user_id.empName}
                </span>
              )}
              {notification.action_user_id?.empName && ' • '}
              {new Date(notification.createdAt).toLocaleString('ar-EG', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              })}
            </DialogDescription>
          </div>
          <div className="flex-shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MessageSquare className="h-4 w-4 text-primary" />
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-lg border bg-card p-4">
            <ScrollArea className="h-[250px] w-full pr-4">
              <div className="space-y-3">{renderMarkdown()}</div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter className="bg-transparent pt-4">
          <Button onClick={handleMarkAsRead} className="w-full sm:w-auto">
            <Check className="mr-2 h-4 w-4" />
            تم القراءة
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
